import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import fetchData from '../../functions/fetchData';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import {
    Card,
    Row,
    Col,
    Statistic,
    Avatar,
    Table,
    Tag,
    Space,
    Typography,
    Button,
    Modal,
    Input,
    InputNumber,
    Select,
    Tooltip,
    Popconfirm,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    BankOutlined,
    WalletOutlined,
    DollarOutlined,
} from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

// API Configuration
const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:3001';
const ENDPOINTS = {
    BANK_ACCOUNTS: `${API_BASE_URL}/api/bank-accounts`,
    BANK_ACCOUNT_BY_ID: (id) => `${API_BASE_URL}/api/bank-accounts/${id}`,
    TOGGLE_STATUS: (id) => `${API_BASE_URL}/api/bank-accounts/${id}/toggle-status`
};

export default function BankManagement() {
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [formData, setFormData] = useState({
        account_name: '',
        bank_name: '',
        account_number: '',
        account_type: 'Savings',
        branch_name: '',
        ifsc_code: '',
        swift_code: '',
        opening_balance: 0,
        notes: ''
    });

    const accountTypes = ['Savings', 'Current', 'Credit', 'Cash', 'UPI'];

    useEffect(() => {
        fetchBankAccounts();
    }, []);

    const fetchBankAccounts = async () => {
        setLoading(true);
        try {
            await fetchData("bank_accounts", setBankAccounts, "id", {});
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
            toast.error('Failed to fetch bank accounts');
        }
        setLoading(false);
    };

    const resetForm = () => {
        setFormData({
            account_name: '',
            bank_name: '',
            account_number: '',
            account_type: 'Savings',
            branch_name: '',
            ifsc_code: '',
            swift_code: '',
            opening_balance: 0,
            notes: ''
        });
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setLoading(true);

        try {
            const requestData = {
                ...formData,
                current_balance: selectedAccount ? selectedAccount.current_balance : formData.opening_balance
            };

            let response;
            if (selectedAccount) {
                response = await axios.put(
                    ENDPOINTS.BANK_ACCOUNT_BY_ID(selectedAccount.id),
                    requestData,
                    getHeaders()
                );
            } else {
                response = await axios.post(
                    `/insertdata/bank_accounts`,
                    requestData,
                    getHeaders()
                );
            }

            if (response.data.success) {
                toast.success(`Bank account ${selectedAccount ? 'updated' : 'created'} successfully`);
                fetchBankAccounts();
                setShowAddModal(false);
                setShowEditModal(false);
                resetForm();
                setSelectedAccount(null);
            }
        } catch (error) {
            console.error('Error saving bank account:', error);
            toast.error(error.response?.data?.message || 'Failed to save bank account');
        }
        setLoading(false);
    };

    const handleEdit = (account) => {
        setSelectedAccount(account);
        setFormData({
            account_name: account.account_name || '',
            bank_name: account.bank_name || '',
            account_number: account.account_number || '',
            account_type: account.account_type || 'Savings',
            branch_name: account.branch_name || '',
            ifsc_code: account.ifsc_code || '',
            swift_code: account.swift_code || '',
            opening_balance: account.opening_balance || 0,
            notes: account.notes || ''
        });
        setShowEditModal(true);
    };

    const handleDelete = async (account) => {
        setLoading(true);
        try {
            const response = await axios.delete(
                `/deletebyid/bank_accounts/id/${account.id}`,
                getHeaders()
            );

            if (response.data.success) {
                toast.success('Bank account deleted successfully');
                fetchBankAccounts();
            } else {
                toast.error(response.data.message || 'Failed to delete bank account');
            }
        } catch (error) {
            console.error('Error deleting bank account:', error);
            toast.error(error.response?.data?.message || 'Failed to delete bank account');
        }
        setLoading(false);
    };

    const toggleStatus = async (account) => {
        setLoading(true);
        try {
            const response = await axios.put(
                ENDPOINTS.TOGGLE_STATUS(account.id),
                {},
                getHeaders()
            );

            if (response.data.success) {
                toast.success(`Account ${account.is_active ? 'deactivated' : 'activated'} successfully`);
                fetchBankAccounts();
            }
        } catch (error) {
            console.error('Error toggling account status:', error);
            toast.error('Failed to update account status');
        }
        setLoading(false);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('th-TH', {
            style: 'currency',
            currency: 'THB'
        }).format(amount);
    };

    const getAccountIcon = (accountType) => {
        switch (accountType) {
            case 'Cash': return <DollarOutlined />;
            case 'UPI': return <WalletOutlined />;
            default: return <BankOutlined />;
        }
    };

    const getAccountIconColor = (accountType) => {
        switch (accountType) {
            case 'Cash': return '#16a34a';
            case 'UPI': return '#2563eb';
            default: return '#0d9488';
        }
    };

    const columns = [
        {
            title: 'Account',
            key: 'account',
            render: (_, account) => (
                <Space>
                    <Avatar
                        shape="square"
                        icon={getAccountIcon(account.account_type)}
                        style={{ backgroundColor: `${getAccountIconColor(account.account_type)}1A`, color: getAccountIconColor(account.account_type) }}
                    />
                    <div>
                        <div><Text strong>{account.account_name}</Text></div>
                        {account.branch_name && <Text type="secondary" style={{ fontSize: 12 }}>{account.branch_name}</Text>}
                    </div>
                </Space>
            )
        },
        { title: 'Bank', dataIndex: 'bank_name', key: 'bank_name' },
        { title: 'Account Number', dataIndex: 'account_number', key: 'account_number', render: (v) => <Text code>{v}</Text> },
        {
            title: 'Type',
            dataIndex: 'account_type',
            key: 'account_type',
            render: (v) => <Tag color={v === 'Cash' ? 'green' : v === 'UPI' ? 'blue' : 'cyan'}>{v}</Tag>
        },
        {
            title: 'Current Balance',
            dataIndex: 'current_balance',
            key: 'current_balance',
            align: 'right',
            sorter: (a, b) => (parseFloat(a.current_balance) || 0) - (parseFloat(b.current_balance) || 0),
            render: (v) => <Text strong style={{ color: '#0d9488' }}>{formatCurrency(parseFloat(v) || 0)}</Text>
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (v) => <Tag color={v ? 'success' : 'error'}>{v ? 'Active' : 'Inactive'}</Tag>
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            render: (_, account) => (
                <Space>
                    <Tooltip title="Edit">
                        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(account)} />
                    </Tooltip>
                    <Tooltip title={account.is_active ? 'Deactivate' : 'Activate'}>
                        <Button
                            size="small"
                            icon={account.is_active ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                            onClick={() => toggleStatus(account)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title={`Delete "${account.account_name}"?`}
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(account)}
                    >
                        <Tooltip title="Delete">
                            <Button size="small" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        },
    ];

    const renderAccountForm = () => (
        <form onSubmit={handleSubmit}>
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Account Name <span style={{ color: '#ff4d4f' }}>*</span></Text>
                        <Input
                            value={formData.account_name}
                            onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                            autoFocus
                            required
                        />
                    </div>
                </Col>
                <Col xs={24} md={12}>
                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Bank Name <span style={{ color: '#ff4d4f' }}>*</span></Text>
                        <Input
                            value={formData.bank_name}
                            onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                            required
                        />
                    </div>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Account Number <span style={{ color: '#ff4d4f' }}>*</span></Text>
                        <Input
                            value={formData.account_number}
                            onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                            required
                        />
                    </div>
                </Col>
                <Col xs={24} md={12}>
                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Account Type</Text>
                        <Select
                            value={formData.account_type}
                            onChange={(value) => setFormData({ ...formData, account_type: value })}
                            style={{ width: '100%' }}
                        >
                            {accountTypes.map(type => (
                                <Option key={type} value={type}>{type}</Option>
                            ))}
                        </Select>
                    </div>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Branch Name</Text>
                        <Input
                            value={formData.branch_name}
                            onChange={(e) => setFormData({ ...formData, branch_name: e.target.value })}
                        />
                    </div>
                </Col>
                <Col xs={24} md={12}>
                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>IFSC Code</Text>
                        <Input
                            value={formData.ifsc_code}
                            onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value })}
                        />
                    </div>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>SWIFT Code</Text>
                        <Input
                            value={formData.swift_code}
                            onChange={(e) => setFormData({ ...formData, swift_code: e.target.value })}
                        />
                    </div>
                </Col>
                <Col xs={24} md={12}>
                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Opening Balance</Text>
                        <InputNumber
                            min={0}
                            step={0.01}
                            value={formData.opening_balance}
                            onChange={(value) => setFormData({ ...formData, opening_balance: value || 0 })}
                            style={{ width: '100%' }}
                        />
                    </div>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24}>
                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Notes</Text>
                        <Input.TextArea
                            rows={3}
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            placeholder="Additional notes or comments"
                        />
                    </div>
                </Col>
            </Row>
            <div style={{ textAlign: 'right' }}>
                <Space>
                    <Button onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); setSelectedAccount(null); }}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {selectedAccount ? 'Update Account' : 'Add Account'}
                    </Button>
                </Space>
            </div>
        </form>
    );

    return (
        <Layout>
            <Header title="Bank Management" />
            <ToastContainer />

            <div style={{ padding: 20 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                    <Col>
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            <BankOutlined style={{ marginRight: 8, color: '#0d9488' }} />
                            Bank Accounts
                        </Typography.Title>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => { resetForm(); setSelectedAccount(null); setShowAddModal(true); }}
                        >
                            Add Bank Account
                        </Button>
                    </Col>
                </Row>

                {/* Summary Cards */}
                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 14, background: '#f0fdfa' }}>
                            <Statistic title="Total Accounts" value={bankAccounts.length} prefix={<BankOutlined />} valueStyle={{ color: '#0d9488' }} />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 14, background: '#f0fdf4' }}>
                            <Statistic title="Active Accounts" value={bankAccounts.filter(acc => acc.is_active).length} prefix={<EyeOutlined />} valueStyle={{ color: '#16a34a' }} />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 14, background: '#eff6ff' }}>
                            <Statistic
                                title="Total Balance"
                                value={bankAccounts.reduce((sum, acc) => sum + (parseFloat(acc.current_balance) || 0), 0)}
                                precision={2}
                                prefix={<WalletOutlined />}
                                suffix="THB"
                                valueStyle={{ color: '#2563eb' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 14, background: '#fffbeb' }}>
                            <Statistic title="Cash Accounts" value={bankAccounts.filter(acc => acc.account_type === 'Cash').length} prefix={<DollarOutlined />} valueStyle={{ color: '#d97706' }} />
                        </Card>
                    </Col>
                </Row>

                {/* Bank Accounts Table */}
                <Card style={{ borderRadius: 14 }} title={<Text strong>Bank Accounts List</Text>}>
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={bankAccounts}
                        loading={loading}
                        pagination={{ pageSize: 10, showSizeChanger: true }}
                        scroll={{ x: true }}
                        locale={{ emptyText: 'No bank accounts found. Add your first bank account to get started.' }}
                    />
                </Card>
            </div>

            {/* Add Modal */}
            <Modal
                title={<Space><PlusOutlined style={{ color: '#0d9488' }} />Add Bank Account</Space>}
                open={showAddModal}
                onCancel={() => { setShowAddModal(false); resetForm(); }}
                footer={null}
                width={680}
            >
                {renderAccountForm()}
            </Modal>

            {/* Edit Modal */}
            <Modal
                title={<Space><EditOutlined style={{ color: '#0d9488' }} />Edit Bank Account</Space>}
                open={showEditModal}
                onCancel={() => { setShowEditModal(false); resetForm(); setSelectedAccount(null); }}
                footer={null}
                width={680}
            >
                {renderAccountForm()}
            </Modal>
        </Layout>
    );
}
